import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiBell, FiArrowLeft, FiActivity, FiAward, FiDownload } from 'react-icons/fi';
import { PageHeader } from '../../theme/components';
import { PageButton } from '../../theme/components/PageButton';
import UpcomingVaccines from './UpcomingVaccines';
import Growth from './Growth';
import Milestones from './Milestones';
import './Reminders.css';

/**
 * Reminders page — tabbed shell.
 *
 *   • UpcomingVaccines — action queue for vaccinations (Upcoming +
 *     Due + Overdue, deduped against the tile buckets).
 *   • Growth           — placeholder, real data when backend is ready
 *   • Milestones       — CDSS watchlist (PENDING + DUE rows, PENDING_WINDOW_DAYS=7 / GRACE_PERIOD_DAYS=30)
 *
 * Tab choice is local state — resets on reload, no URL pollution.
 * ponytail: lift to URL params when tabs need to be shareable.
 */
const TABS = [
    { key: 'upcoming', label: 'Vaccinations', icon: FiBell, Component: UpcomingVaccines },
    { key: 'growth', label: 'Growth', icon: FiActivity, Component: Growth },
    { key: 'milestones', label: 'Milestones', icon: FiAward, Component: Milestones },
];

const Reminders = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // ?filter=due|upcoming — pre-applies the queue filter on first
    // mount (e.g. from UpcomingVaccinationsReport's "View child list").
    // Anything else falls back to 'all'. ponytail: URL state for the
    // filter only — tab choice stays local since no page links to a
    // specific tab today.
    const initialFilter = (() => {
        const f = searchParams.get('filter');
        return f === 'due' || f === 'upcoming' ? f : 'all';
    })();
    // ?tab=upcoming|growth|milestones — pre-selects a tab on mount
    // (e.g. Dashboard's Milestones Status card deep-links here). Falls
    // back to 'upcoming' for any other / missing value.
    const validTabKeys = TABS.map((t) => t.key);
    const initialTab = (() => {
        const t = searchParams.get('tab');
        return validTabKeys.includes(t) ? t : 'upcoming';
    })();
    const [activeKey, setActiveKey] = useState(initialTab);
    // The active Vaccinations tab publishes its export trigger + a
    // canExport flag up here via a callback, so we can render the
    // Export PDF button in the tabs row and re-render when the queue
    // loads/empties. ponytail: callback over ref + state bridge — refs
    // don't trigger re-renders, so the button would stay disabled.
    const [exporter, setExporter] = useState(null);
    const Active = TABS.find((t) => t.key === activeKey) ?? TABS[0];
    const ActiveComponent = Active.Component;

    return (
        <div className="rem-wrapper">
            <PageHeader
                title="Reminders"
                icon={FiBell}
                actions={
                    <PageButton
                        type="button"
                        variant="ghost"
                        startIcon={<FiArrowLeft size={16} />}
                        onClick={() => navigate('/home')}
                    >
                        Back
                    </PageButton>
                }
            />

            <nav className="rem-tabs" role="tablist" aria-label="Reminder categories">
                <div className="rem-tabs__list">
                    {TABS.map(({ key, label, icon: Icon }) => {
                        const isActive = key === activeKey;
                        return (
                            <button
                                key={key}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                className={`rem-tab${isActive ? ' rem-tab--active' : ''}`}
                                onClick={() => setActiveKey(key)}
                            >
                                <Icon size={16} aria-hidden="true" />
                                <span>{label}</span>
                            </button>
                        );
                    })}
                </div>
                {/* Export PDF — lives in the tabs row, contextual to whichever
                   tab is active. Each tab publishes its own exportPDF +
                   canExport via the onExportChange callback, so the button
                   disables itself automatically when the active tab has
                   nothing to export. Tabs wrapped in their own flex group
                   so the button sits flush right regardless of tab count. */}
                <PageButton
                    type="button"
                    variant="primary"
                    startIcon={<FiDownload size={16} />}
                    onClick={() => exporter?.exportPDF()}
                    disabled={!exporter?.canExport}
                    className="rem-export-btn"
                >
                    Export PDF
                </PageButton>
            </nav>

            <ActiveComponent onExportChange={setExporter} initialFilter={initialFilter} />
        </div>
    );
};

export default Reminders;